import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/* =============================================================================
   Layers: mission panel, modals, notification panel, drawers, filter sheet.
   Matches the mockup's open/close model: the element mounts hidden, gets `.open`
   on the next frame (CSS transition), and unmounts 260ms after close.
   Escape / backdrop click close the TOP layer only (via onRequestClose, so forms
   can run their unsaved-changes guard first).
   ========================================================================== */

interface Entry {
  id: number;
  close: () => void;
  clear: boolean;
}
const stack: Entry[] = [];
const subs = new Set<() => void>();
const notify = () => subs.forEach((f) => f());
let seq = 0;

function useStack(): Entry[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => {
      subs.delete(f);
    };
  }, []);
  return stack;
}

export const layerCount = (): number => stack.length;

/** Close the top-most layer (Escape). Returns false when nothing was open. */
export function closeTopLayer(): boolean {
  const top = stack[stack.length - 1];
  if (!top) return false;
  top.close();
  return true;
}

if (typeof document !== 'undefined') {
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    // designed dialogs / popovers / version gate handle their own Escape
    if (document.querySelector('.ui-dialog-wrap, .ui-pop, .ui-sheet, .ui-version-block')) return;
    if (closeTopLayer()) e.preventDefault();
  });
}

export interface LayerProps {
  open: boolean;
  onRequestClose: () => void;
  className: string;
  /** transparent backdrop on desktop (notification panel) */
  clear?: boolean;
  as?: 'aside' | 'section' | 'div';
  role?: string;
  label?: string;
  labelledBy?: string;
  modal?: boolean;
  style?: CSSProperties;
  data?: Record<string, string | undefined>;
  children: ReactNode;
}

export function Layer({ open, onRequestClose, className, clear, as = 'section', role = 'dialog', label, labelledBy, modal = true, style, data, children }: LayerProps) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const idRef = useRef(0);
  const closeRef = useRef(onRequestClose);
  closeRef.current = onRequestClose;
  const elRef = useRef<HTMLElement | null>(null);
  const prevFocus = useRef<Element | null>(null);

  useEffect(() => {
    if (open) {
      prevFocus.current = document.activeElement;
      setMounted(true);
      idRef.current = ++seq;
      stack.push({ id: idRef.current, close: () => closeRef.current(), clear: !!clear });
      notify();
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => {
        cancelAnimationFrame(r);
        const i = stack.findIndex((x) => x.id === idRef.current);
        if (i >= 0) stack.splice(i, 1);
        notify();
      };
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), 260);
    if (prevFocus.current instanceof HTMLElement) prevFocus.current.focus({ preventScroll: true });
    return () => clearTimeout(t);
  }, [open, clear]);

  useEffect(() => {
    if (shown && elRef.current && !elRef.current.contains(document.activeElement)) {
      const f = elRef.current.querySelector<HTMLElement>('[autofocus], [data-autofocus]') ?? elRef.current;
      f.focus({ preventScroll: true });
    }
  }, [shown]);

  const st = useStack();
  if (!mounted) return null;
  const idx = st.findIndex((x) => x.id === idRef.current);
  const z = 100 + Math.max(0, idx) * 10;
  const Tag = as;
  const dataAttrs = Object.fromEntries(Object.entries(data ?? {}).map(([k, v]) => [`data-${k}`, v]));
  return createPortal(
    <>
      <div className={`backdrop${shown ? ' show' : ''}${clear ? ' clear' : ''}`} style={{ zIndex: z - 1 }} onClick={() => closeRef.current()} aria-hidden="true" />
      <Tag
        ref={(el: HTMLElement | null) => {
          elRef.current = el;
        }}
        className={`${className}${shown ? ' open' : ''}`}
        style={{ ...style, zIndex: z }}
        role={role}
        aria-modal={modal || undefined}
        aria-label={label}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        {...dataAttrs}
      >
        {children}
      </Tag>
    </>,
    document.body,
  );
}
